import { useMemo, useState, useRef, useEffect, memo } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table'
import { useEnhancedBrandStore } from '../../stores/enhancedBrandStore'
import { ParsedBrand } from '../../types/brand'
import { ExportButton } from './ExportButton'
import { useAuth } from '../../contexts/AuthContext'
import { EyeIcon, PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline'

interface BrandTableProps {
  onRowClick?: (brand: ParsedBrand) => void
  onAddBrandClick?: () => void
}

function formatWebsiteLink(website: string, brandName: string) {
  if (!website || website === 'unknown') return 'No website'
  
  // Ensure URL has protocol
  const url = website.startsWith('http') ? website : `https://${website}`
  
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 underline"
      onClick={(e) => e.stopPropagation()}
    >
      {brandName}
    </a>
  )
}

function formatCategories(categories: string[], maxShow: number = 3) {
  if (!categories || categories.length === 0) return 'None'
  
  const shown = categories.slice(0, maxShow)
  const remaining = categories.length - maxShow
  
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((cat, idx) => (
        <span
          key={idx}
          className="inline-block px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
        >
          {cat}
        </span>
      ))}
      {remaining > 0 && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          +{remaining} more
        </span>
      )}
    </div>
  )
}

function getConfidenceColor(score: number) {
  if (score >= 8) return 'text-green-600 dark:text-green-400'
  if (score >= 6) return 'text-yellow-600 dark:text-yellow-400'
  if (score >= 4) return 'text-orange-600 dark:text-orange-400'
  return 'text-red-600 dark:text-red-400'
}

function formatManufacturingStatus(status: string) {
  const statusMap = {
    confirmed_usa: { label: 'Confirmed USA', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
    likely_usa: { label: 'Likely USA', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
    mixed: { label: 'Mixed', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
    imported: { label: 'Imported', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
    unknown: { label: 'Unknown', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200' }
  }
  
  const mapped = statusMap[status as keyof typeof statusMap] || statusMap.unknown
  
  return (
    <span className={`inline-block px-2 py-1 text-xs rounded-full ${mapped.color}`}>
      {mapped.label}
    </span>
  )
}

export const BrandTable = memo(function BrandTable({ onRowClick, onAddBrandClick }: BrandTableProps) {
  const { filteredBrands, isLoading } = useEnhancedBrandStore()
  const { hasPermission } = useAuth()
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [preservedPageIndex, setPreservedPageIndex] = useState(0)
  const [preservedPageSize, setPreservedPageSize] = useState(25)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const scrollPositionRef = useRef(0)

  const handleEdit = (brand: ParsedBrand, e: React.MouseEvent) => {
    e.stopPropagation()
    // TODO: Implement edit functionality
    console.log('Edit brand:', brand.brand)
  }

  const handleDelete = (brand: ParsedBrand, e: React.MouseEvent) => {
    e.stopPropagation()
    // TODO: Implement delete functionality
    console.log('Delete brand:', brand.brand)
  }

  const handleView = (brand: ParsedBrand, e: React.MouseEvent) => {
    e.stopPropagation()
    onRowClick?.(brand)
  }

  const columns = useMemo<ColumnDef<ParsedBrand>[]>(
    () => [
      {
        accessorKey: 'brand',
        header: 'Brand',
        cell: ({ row, getValue }) => (
          <div className="flex flex-col">
            {formatWebsiteLink(row.original.website, getValue() as string)}
            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {row.original.hq_location !== 'unknown' ? row.original.hq_location : 'Location unknown'}
            </span>
          </div>
        ),
        size: 200,
      },
      {
        accessorKey: 'manufacturing_verification',
        header: 'Manufacturing',
        cell: ({ getValue }) => formatManufacturingStatus(getValue() as string),
        size: 140,
      },
      {
        accessorKey: 'amazon_presence',
        header: 'Amazon',
        cell: ({ getValue }) => {
          const value = getValue() as string
          const formatted = value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
          return (
            <span className="text-sm">
              {formatted}
            </span>
          )
        },
        size: 100,
      },
      {
        accessorKey: 'clean_categories',
        header: 'Categories',
        cell: ({ getValue }) => formatCategories(getValue() as string[]),
        size: 250,
        enableSorting: false,
      },
      {
        accessorKey: 'price_tier',
        header: 'Price Tier',
        cell: ({ getValue }) => {
          const value = getValue() as string
          const formatted = value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
          return <span className="text-sm">{formatted}</span>
        },
        size: 100,
      },
      {
        accessorKey: 'founded_year',
        header: 'Founded',
        cell: ({ getValue }) => {
          const year = getValue()
          return (
            <span className="text-sm">
              {typeof year === 'number' ? year : 'Unknown'}
            </span>
          )
        },
        size: 80,
      },
      {
        accessorKey: 'confidence_score',
        header: 'Confidence',
        cell: ({ getValue }) => {
          const score = getValue() as number
          return (
            <div className="flex items-center space-x-2">
              <span className={`font-medium ${getConfidenceColor(score)}`}>
                {score}/10
              </span>
              <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    score >= 8 ? 'bg-green-500' :
                    score >= 6 ? 'bg-yellow-500' :
                    score >= 4 ? 'bg-orange-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${(score / 10) * 100}%` }}
                />
              </div>
            </div>
          )
        },
        size: 140,
      },
      {
        accessorKey: 'consumer_summary',
        header: 'Description',
        cell: ({ getValue }) => {
          const summary = getValue() as string
          return (
            <div className="text-sm text-gray-600 dark:text-gray-400 max-w-xs">
              <p className="line-clamp-2">
                {summary.length > 100 ? `${summary.substring(0, 100)}...` : summary}
              </p>
            </div>
          )
        },
        size: 300,
        enableSorting: false,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const brand = row.original
          return (
            <div className="flex items-center space-x-1">
              {/* View Action - Available to all users */}
              <button
                onClick={(e) => handleView(brand, e)}
                className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                title="View Details"
              >
                <EyeIcon className="h-4 w-4" />
              </button>
              
              {/* Edit Action - Available to editors and admins */}
              {hasPermission('edit_brands') && (
                <button
                  onClick={(e) => handleEdit(brand, e)}
                  className="p-1 text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                  title="Edit Brand"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              )}
              
              {/* Delete Action - Available to admins only */}
              {hasPermission('delete_brands') && (
                <button
                  onClick={(e) => handleDelete(brand, e)}
                  className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  title="Delete Brand"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          )
        },
        size: 80,
        enableSorting: false,
      },
    ],
    [hasPermission, onRowClick]
  )

  // Preserve scroll position and page state during filter changes
  useEffect(() => {
    if (tableContainerRef.current) {
      scrollPositionRef.current = tableContainerRef.current.scrollTop
    }
  }, [filteredBrands])

  // Restore scroll position after render
  useEffect(() => {
    if (tableContainerRef.current && scrollPositionRef.current > 0) {
      tableContainerRef.current.scrollTop = scrollPositionRef.current
    }
  })

  const table = useReactTable({
    data: filteredBrands,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageIndex: preservedPageIndex,
        pageSize: preservedPageSize,
      },
    },
  })

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 dark:bg-gray-750 rounded mb-2"></div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow layout-transition">
      {/* Table Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Brand Details
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Click any row to view detailed brand information
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {table.getFilteredRowModel().rows.length} brands
            </span>
            {hasPermission('create_brands') && onAddBrandClick && (
              <button
                onClick={onAddBrandClick}
                className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
                <span>Add</span>
              </button>
            )}
            <ExportButton />
            <select
              value={table.getState().pagination.pageSize}
              onChange={e => {
                const newPageSize = Number(e.target.value)
                setPreservedPageSize(newPageSize)
                table.setPageSize(newPageSize)
              }}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              {[25, 50, 100, 200].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div ref={tableContainerRef} className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-200 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
                    style={{ width: header.getSize() }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center space-x-1">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <span className="text-gray-400">
                          {header.column.getIsSorted() === 'asc' ? '↑' :
                           header.column.getIsSorted() === 'desc' ? '↓' : '↕'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                onClick={() => onRowClick?.(row.original)}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
              >
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100"
                    style={{ width: cell.column.getSize() }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setPreservedPageIndex(0)
                table.setPageIndex(0)
              }}
              disabled={!table.getCanPreviousPage()}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded"
            >
              First
            </button>
            <button
              onClick={() => {
                const newIndex = table.getState().pagination.pageIndex - 1
                setPreservedPageIndex(newIndex)
                table.previousPage()
              }}
              disabled={!table.getCanPreviousPage()}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded"
            >
              Previous
            </button>
            <button
              onClick={() => {
                const newIndex = table.getState().pagination.pageIndex + 1
                setPreservedPageIndex(newIndex)
                table.nextPage()
              }}
              disabled={!table.getCanNextPage()}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded"
            >
              Next
            </button>
            <button
              onClick={() => {
                const lastIndex = table.getPageCount() - 1
                setPreservedPageIndex(lastIndex)
                table.setPageIndex(lastIndex)
              }}
              disabled={!table.getCanNextPage()}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded"
            >
              Last
            </button>
          </div>
          
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {table.getState().pagination.pageIndex + 1} of{' '}
              {table.getPageCount()}
            </span>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Go to:</span>
              <input
                type="number"
                min="1"
                max={table.getPageCount()}
                defaultValue={table.getState().pagination.pageIndex + 1}
                onChange={e => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0
                  setPreservedPageIndex(page)
                  table.setPageIndex(page)
                }}
                className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})